import { useI18n, type MessageKey } from '@ecom/i18n';
import { Button } from '@ecom/ui-kit';
import { type ReactElement, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import type { AssistantEvent, ProposedOperation } from '../types';

import { downloadInvoicePdf } from '@/features/invoices/api';
import { useUploadProductImageMutation } from '@/features/products/api';
import { useUploadStoreBrandingMutation, type BrandingKind } from '@/features/store/api';
import { useSession } from '@/lib/session';
import { uploadErrorMessage } from '@/lib/upload-error';

/** Dashboard routes the assistant may link to (`open_dashboard_page` in the API) — and nothing else. */
const PAGE_PATHS: Readonly<Record<string, string>> = {
  dashboard: '/dashboard',
  store: '/store',
  products: '/products',
  inventory: '/inventory',
  categories: '/categories',
  orders: '/orders',
  customers: '/customers',
  delivery: '/delivery',
  delivery_account: '/delivery/account',
  invoices: '/invoices',
  promotions: '/promotions',
  marketing: '/marketing',
  payments: '/payments',
  analytics: '/analytics',
  team: '/team',
  settings: '/settings',
  account: '/account',
  todo: '/todo',
};

const NOTICE_KEY: Readonly<Record<string, MessageKey>> = {
  refusal: 'merchant.assistant.notice.refusal',
  output_limit: 'merchant.assistant.notice.outputLimit',
  step_limit: 'merchant.assistant.notice.stepLimit',
  conversation_full: 'merchant.assistant.notice.conversationFull',
  declined: 'merchant.assistant.notice.declined',
};

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function field(input: unknown, name: string): string | undefined {
  if (typeof input !== 'object' || input === null) return undefined;
  const value = (input as Record<string, unknown>)[name];
  return typeof value === 'string' ? value : undefined;
}

export function EventView({ event }: { event: AssistantEvent }): ReactElement | null {
  const { t } = useI18n();
  switch (event.type) {
    case 'text':
      return (
        <div className="max-w-[90%] self-start whitespace-pre-wrap rounded-2xl rounded-ss-sm bg-surface2 px-3 py-2 text-sm text-ink">
          {event.text}
        </div>
      );
    case 'operation':
      return (
        <div
          role="status"
          className={`self-start rounded-md border px-3 py-1.5 text-xs ${
            event.ok
              ? 'border-good bg-goodSoft text-good'
              : 'border-critical bg-criticalSoft text-critical'
          }`}
        >
          <span aria-hidden="true">{event.ok ? '✓ ' : '✗ '}</span>
          {event.label} —{' '}
          {event.ok
            ? t('merchant.assistant.operation.succeeded')
            : t('merchant.assistant.operation.failed', { status: String(event.status) })}
        </div>
      );
    case 'secret':
      return <SecretCard label={event.label} value={event.value} />;
    case 'client_action':
      return <ClientActionCard action={event.action} input={event.input} />;
    case 'notice':
      return (
        <p className="self-center text-center text-xs text-inkFaint">
          {t(NOTICE_KEY[event.code] ?? 'merchant.assistant.notice.stepLimit')}
        </p>
      );
  }
}

function SecretCard({ label, value }: { label: string; value: string }): ReactElement {
  const { t } = useI18n();
  const [copied, setCopied] = useState(false);
  return (
    <div className="self-stretch rounded-md border border-warn bg-warnSoft p-3 text-sm">
      <p className="font-medium text-ink">{label}</p>
      <p className="mt-1 text-xs text-inkSoft">{t('merchant.assistant.secret.hint')}</p>
      <div className="mt-2 flex items-center gap-2">
        <code className="rounded bg-surface px-2 py-1 font-mono text-sm text-ink" dir="ltr">
          {value}
        </code>
        <Button
          size="sm"
          variant="secondary"
          onClick={() => {
            void navigator.clipboard.writeText(value).then(() => {
              setCopied(true);
            });
          }}
        >
          {copied ? t('merchant.assistant.secret.copied') : t('merchant.assistant.secret.copy')}
        </Button>
      </div>
    </div>
  );
}

function ClientActionCard({
  action,
  input,
}: {
  action: string;
  input: unknown;
}): ReactElement | null {
  const { t } = useI18n();
  if (action === 'request_image_upload') {
    const target = field(input, 'target');
    const productId = field(input, 'productId');
    if (target === 'product' && productId !== undefined && UUID.test(productId)) {
      return <ProductImageUpload productId={productId} />;
    }
    if (target === 'logo' || target === 'banner') return <BrandingUpload kind={target} />;
    return null;
  }
  if (action === 'request_invoice_download') {
    const invoiceId = field(input, 'invoiceId');
    return invoiceId !== undefined && UUID.test(invoiceId) ? (
      <InvoiceDownload invoiceId={invoiceId} />
    ) : null;
  }
  if (action === 'open_dashboard_page') {
    const path = PAGE_PATHS[field(input, 'page') ?? ''];
    return path === undefined ? null : (
      <Link to={path} className="self-start text-sm font-medium text-accent underline">
        {t('merchant.assistant.action.openPage')} →
      </Link>
    );
  }
  return null;
}

function FilePicker({
  label,
  busy,
  onFile,
}: {
  label: string;
  busy: boolean;
  onFile: (file: File) => void;
}): ReactElement {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <Button
        size="sm"
        variant="secondary"
        loading={busy}
        onClick={() => inputRef.current?.click()}
      >
        {label}
      </Button>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file !== undefined) onFile(file);
        }}
      />
    </>
  );
}

function ActionFrame({
  children,
  outcome,
}: {
  children: ReactElement;
  outcome: string | null;
}): ReactElement {
  return (
    <div className="flex flex-col gap-1 self-start rounded-md border border-line bg-surface p-3">
      {children}
      {outcome !== null ? <p className="text-xs text-inkSoft">{outcome}</p> : null}
    </div>
  );
}

function ProductImageUpload({ productId }: { productId: string }): ReactElement {
  const { t } = useI18n();
  const upload = useUploadProductImageMutation(productId);
  const [outcome, setOutcome] = useState<string | null>(null);
  return (
    <ActionFrame outcome={outcome}>
      <FilePicker
        label={t('merchant.assistant.action.uploadPhoto')}
        busy={upload.isPending}
        onFile={(file) => {
          upload.mutate(
            { file },
            {
              onSuccess: () => {
                setOutcome(t('merchant.assistant.action.uploaded'));
              },
              onError: (error) => {
                setOutcome(uploadErrorMessage(error, t));
              },
            },
          );
        }}
      />
    </ActionFrame>
  );
}

function BrandingUpload({ kind }: { kind: BrandingKind }): ReactElement {
  const { t } = useI18n();
  const upload = useUploadStoreBrandingMutation(kind);
  const [outcome, setOutcome] = useState<string | null>(null);
  return (
    <ActionFrame outcome={outcome}>
      <FilePicker
        label={t(
          kind === 'logo'
            ? 'merchant.assistant.action.uploadLogo'
            : 'merchant.assistant.action.uploadBanner',
        )}
        busy={upload.isPending}
        onFile={(file) => {
          upload.mutate(file, {
            onSuccess: () => {
              setOutcome(t('merchant.assistant.action.uploaded'));
            },
            onError: (error) => {
              setOutcome(uploadErrorMessage(error, t));
            },
          });
        }}
      />
    </ActionFrame>
  );
}

function InvoiceDownload({ invoiceId }: { invoiceId: string }): ReactElement {
  const { t } = useI18n();
  const { accessToken, activeShopId } = useSession();
  const [busy, setBusy] = useState(false);
  const [outcome, setOutcome] = useState<string | null>(null);
  return (
    <ActionFrame outcome={outcome}>
      <Button
        size="sm"
        variant="secondary"
        loading={busy}
        onClick={() => {
          setBusy(true);
          downloadInvoicePdf(invoiceId, invoiceId.slice(0, 8), accessToken, activeShopId)
            .then(() => {
              setOutcome(null);
            })
            .catch(() => {
              setOutcome(t('merchant.invoices.downloadFailed'));
            })
            .finally(() => {
              setBusy(false);
            });
        }}
      >
        {t('merchant.assistant.action.downloadInvoice')}
      </Button>
    </ActionFrame>
  );
}

/** One write waiting for the merchant: exactly what will be sent, then Confirm / Cancel. */
export function PendingCard({
  proposals,
  busy,
  onDecide,
}: {
  proposals: readonly ProposedOperation[];
  busy: boolean;
  onDecide: (approve: boolean) => void;
}): ReactElement {
  const { t } = useI18n();
  return (
    <div
      className="self-stretch rounded-md border border-accent bg-accentSoft p-3 text-sm"
      role="group"
      aria-label={t('merchant.assistant.pending.title')}
    >
      <p className="font-semibold text-ink">{t('merchant.assistant.pending.title')}</p>
      <ul className="mt-2 flex flex-col gap-2">
        {proposals.map((proposal) => (
          <li key={proposal.id}>
            <p className="font-medium text-ink">{proposal.label}</p>
            <dl className="mt-1 grid grid-cols-[auto,1fr] gap-x-2 gap-y-0.5 text-xs text-inkSoft">
              {detailsOf(proposal.input).map(([key, value]) => (
                <div key={key} className="contents">
                  <dt className="font-mono">{key}</dt>
                  <dd className="break-all" dir="auto">
                    {value}
                  </dd>
                </div>
              ))}
            </dl>
          </li>
        ))}
      </ul>
      <div className="mt-3 flex gap-2">
        <Button
          size="sm"
          loading={busy}
          onClick={() => {
            onDecide(true);
          }}
        >
          {t('merchant.assistant.pending.confirm')}
        </Button>
        <Button
          size="sm"
          variant="secondary"
          disabled={busy}
          onClick={() => {
            onDecide(false);
          }}
        >
          {t('merchant.assistant.pending.cancel')}
        </Button>
      </div>
    </div>
  );
}

/** The proposal's input, flattened one level (`body.price` → « price »), values as text. */
export function detailsOf(input: unknown): [string, string][] {
  if (typeof input !== 'object' || input === null) return [];
  const rows: [string, string][] = [];
  for (const [key, value] of Object.entries(input as Record<string, unknown>)) {
    if ((key === 'body' || key === 'query') && typeof value === 'object' && value !== null) {
      for (const [inner, innerValue] of Object.entries(value as Record<string, unknown>)) {
        rows.push([inner, display(innerValue)]);
      }
    } else {
      rows.push([key, display(value)]);
    }
  }
  return rows;
}

function display(value: unknown): string {
  if (value === null) return '—';
  return typeof value === 'string' ? value : JSON.stringify(value);
}
